import React, { useState, useEffect, useCallback } from "react";
import { colors, font, displayName, generateId } from "../utils/theme.js";
import { api } from "../api/client.js";
import { section, inputStyle, primaryBtn, ghostBtn, editBtn } from "../clients/ui.js";

// Task Templates (increment 7). Author reusable objectives (one objective + its tasks) that
// get STAMPED onto a client project or an internal objective via the import flow elsewhere.
const ASSIGNEE_OPTIONS = [
  { v: "unassigned", label: "Unassigned" },
  { v: "role:projectManager", label: "Role · Project Manager" },
  { v: "role:accountOwner", label: "Role · Account Owner" },
  { v: "client", label: "The client" },
];
function assigneeToValue(a) {
  if (!a || a.type === "unassigned") return "unassigned";
  if (a.type === "client") return "client";
  if (a.type === "role") return `role:${a.role}`;
  return a.userId; // specific user
}
function valueToAssignee(v) {
  if (v === "unassigned") return { type: "unassigned" };
  if (v === "client") return { type: "client" };
  if (v.startsWith("role:")) return { type: "role", role: v.slice(5) };
  return { type: "user", userId: v };
}

export default function TemplatesPage({ users }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template object being edited, or null

  const load = useCallback(async () => {
    setLoading(true);
    try { setTemplates(await api.templates.list() || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, color: colors.textMuted }}>Loading templates…</div>;

  if (editing) {
    return <TemplateEditor template={editing} users={users}
      onBack={() => setEditing(null)}
      onSaved={async () => { setEditing(null); await load(); }} />;
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: colors.text, letterSpacing: -0.3 }}>Task Templates</h1>
        <div style={{ flex: 1 }} />
        <button onClick={() => setEditing({ name: "", instructions: "", tasks: [] })} style={primaryBtn}>+ New template</button>
      </div>
      <p style={{ color: colors.textMuted, fontSize: 13, marginTop: 0, maxWidth: 640 }}>
        A template is one objective plus its tasks (e.g. “Onboarding”). Import it onto a client project or an internal objective; each import is an independent copy.
      </p>

      {templates.length === 0 ? (
        <div style={{ color: colors.textDim, fontSize: 14, padding: "20px 0" }}>No templates yet — create one with “+ New template”.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {templates.map((t) => (
            <div key={t.id} onClick={() => setEditing(t)} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 16, cursor: "pointer" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>{t.name}</div>
              <div style={{ fontSize: 12, color: colors.textDim, marginTop: 4 }}>{(t.tasks || []).length} task{(t.tasks || []).length === 1 ? "" : "s"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ template, users, onBack, onSaved }) {
  const [name, setName] = useState(template.name || "");
  const [instructions, setInstructions] = useState(template.instructions || "");
  const [isPrivate, setIsPrivate] = useState(template.private === true);
  const [tasks, setTasks] = useState(() => (template.tasks || []).map((t) => ({ ...t, _k: generateId() })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isNew = !template.id;
  const setTask = (k, field, val) => setTasks((ts) => ts.map((t) => (t._k === k ? { ...t, [field]: val } : t)));
  const addTask = () => setTasks((ts) => [...ts, { _k: generateId(), name: "", instructions: "", startOffsetDays: "", dueOffsetDays: "", defaultAssignee: { type: "unassigned" }, optional: false, private: false }]);
  const removeTask = (k) => setTasks((ts) => ts.filter((t) => t._k !== k));

  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true); setError(null);
    const payload = {
      name: name.trim(), instructions, private: isPrivate,
      tasks: tasks.map((t) => ({
        name: t.name, instructions: t.instructions,
        startOffsetDays: t.startOffsetDays === "" ? null : Number(t.startOffsetDays),
        dueOffsetDays: t.dueOffsetDays === "" ? null : Number(t.dueOffsetDays),
        defaultAssignee: t.defaultAssignee,
        optional: t.optional === true,
        private: t.private === true,
      })),
    };
    const res = isNew ? await api.templates.create(payload) : await api.templates.update(template.id, payload);
    setBusy(false);
    if (res.ok) onSaved();
    else setError(res.data?.error || "Couldn’t save the template.");
  };

  const del = async () => {
    if (!template.id) return;
    setBusy(true);
    await api.templates.remove(template.id);
    setBusy(false); onSaved();
  };

  const L = ({ children }) => <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: colors.textDim, margin: "0 0 4px" }}>{children}</div>;

  return (
    <div>
      <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 10 }}>
        <button onClick={onBack} style={{ ...editBtn, color: colors.textMuted }}>Task Templates</button> <span>/ {isNew ? "New template" : name || "Template"}</span>
      </div>

      <div style={section}>
        <L>Objective name *</L>
        <input autoFocus style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Onboarding" />
        <div style={{ marginTop: 12 }}><L>Objective instructions</L>
          <textarea style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Optional" />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 12, color: colors.textMuted, cursor: "pointer", userSelect: "none" }} title="Imported objective and all its tasks are hidden from client updates">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
          Private objective
        </label>
      </div>

      <div style={{ ...section }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase" }}>Tasks</div>
          <button onClick={addTask} style={editBtn}>+ Add task</button>
        </div>
        <div style={{ fontSize: 11, color: colors.textDim, marginBottom: 10 }}>Offsets are <b>working days</b> (Mon–Fri) from the objective start date chosen at import. Leave blank to import with no date.</div>

        {tasks.length === 0 && <div style={{ color: colors.textDim, fontSize: 13 }}>No tasks yet.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {tasks.map((t) => (
            <div key={t._k} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12, background: t.optional ? colors.bg : "transparent" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input style={{ ...inputStyle, flex: 1 }} value={t.name} onChange={(e) => setTask(t._k, "name", e.target.value)} placeholder="Task name" />
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: colors.textMuted, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }} title="Optional tasks are skipped at import unless the importer opts in.">
                  <input type="checkbox" checked={t.optional === true} onChange={(e) => setTask(t._k, "optional", e.target.checked)} />
                  Optional
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: colors.textMuted, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }} title="Imported task is hidden from client updates.">
                  <input type="checkbox" checked={t.private === true} onChange={(e) => setTask(t._k, "private", e.target.checked)} />
                  Private
                </label>
                <button onClick={() => removeTask(t._k)} style={{ ...ghostBtn, color: colors.danger, borderColor: colors.border }}>Remove</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr", gap: 8, marginTop: 8 }}>
                <div><L>Start offset (days)</L><input type="number" style={inputStyle} value={t.startOffsetDays ?? ""} onChange={(e) => setTask(t._k, "startOffsetDays", e.target.value)} placeholder="blank" /></div>
                <div><L>Due offset (days)</L><input type="number" style={inputStyle} value={t.dueOffsetDays ?? ""} onChange={(e) => setTask(t._k, "dueOffsetDays", e.target.value)} placeholder="blank" /></div>
                <div><L>Default assignee</L>
                  <select style={inputStyle} value={assigneeToValue(t.defaultAssignee)} onChange={(e) => setTask(t._k, "defaultAssignee", valueToAssignee(e.target.value))}>
                    {ASSIGNEE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    <optgroup label="Specific person">
                      {(users || []).map((u) => <option key={u.id} value={u.id}>{displayName(u)}</option>)}
                    </optgroup>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 8 }}><L>Default instructions</L>
                <textarea style={{ ...inputStyle, minHeight: 40, resize: "vertical" }} value={t.instructions} onChange={(e) => setTask(t._k, "instructions", e.target.value)} placeholder="Copied onto the imported task (optional)" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && <div style={{ color: colors.danger, fontSize: 13, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button onClick={save} disabled={!name.trim() || busy} style={{ ...primaryBtn, opacity: !name.trim() || busy ? 0.5 : 1 }}>{busy ? "Saving…" : "Save template"}</button>
        <button onClick={onBack} style={ghostBtn}>Cancel</button>
        <div style={{ flex: 1 }} />
        {!isNew && <button onClick={del} disabled={busy} style={{ ...ghostBtn, color: colors.danger }}>Delete template</button>}
      </div>
    </div>
  );
}
